const API_BASE = '/api';
const AUTH_BASE = '/auth';

export interface AuthUser {
  id: string;
  email: string;
  role: 'admin' | 'user';
}

export interface AppInfo {
  name: string;
  hasData?: boolean;
}

export interface LogEntry {
  id?: string;
  appName: string;
  timestamp: string;
  level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'UNKNOWN';
  message: string;
  raw: string;
}

export interface LogEntryDetails extends LogEntry {
  clientIp?: string;
  httpMethod?: string;
  urlPath?: string;
  urlQuery?: string;
  statusCode?: number;
  responseTimeMs?: number;
  userAgent?: string;
  referer?: string;
  requestId?: string;
  category?: string;
  // Additional forensic fields
  hostHeader?: string;
  serverPort?: number;
  requestBytes?: number;
  responseBytes?: number;
  protocol?: string;
  serverName?: string;
  authenticatedUser?: string;
}

export interface SearchResult {
  entries: LogEntry[];
  total: number;
  hasMore: boolean;
}

export interface SearchParams {
  q?: string;
  from?: string;
  to?: string;
  level?: string[];
  limit?: number;
  offset?: number;
}

export interface UserInfo {
  id: string;
  email: string;
  role: 'admin' | 'user';
  isActive: boolean;
  createdAt: string;
}

// ==================== Auth API ====================

export async function checkAuth(): Promise<AuthUser | null> {
  try {
    const response = await fetch(`${AUTH_BASE}/me`, {
      credentials: 'include',
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data.user;
  } catch {
    return null;
  }
}

export async function requestMagicLink(email: string): Promise<{ message: string }> {
  const response = await fetch(`${AUTH_BASE}/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function logout(): Promise<void> {
  await fetch(`${AUTH_BASE}/logout`, {
    method: 'POST',
    credentials: 'include',
  });
}

// ==================== Admin API ====================

export async function getUsers(): Promise<UserInfo[]> {
  const response = await fetch(`${AUTH_BASE}/users`, {
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  const data = await response.json();
  return data.users;
}

export async function inviteUser(email: string, role: 'admin' | 'user' = 'user'): Promise<void> {
  const response = await fetch(`${AUTH_BASE}/invite`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, role }),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
}

export async function deleteUser(userId: string): Promise<void> {
  const response = await fetch(`${AUTH_BASE}/users/${userId}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
}

export async function revokeUserSessions(userId: string): Promise<void> {
  const response = await fetch(`${AUTH_BASE}/users/${userId}/revoke`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
}

export async function toggleUserStatus(userId: string): Promise<{ isActive: boolean }> {
  const response = await fetch(`${AUTH_BASE}/users/${userId}/toggle`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// ==================== Stats API ====================

export interface GlobalStats {
  totalLogs: number;
  oldestLog?: string;
  newestLog?: string;
  databaseSize: number;
}

export async function getGlobalStats(): Promise<GlobalStats> {
  const response = await fetchWithAuth('/stats');
  return response.json();
}

// ==================== Auth Error Handling ====================

// Custom event for auth failures - App component listens for this
export const AUTH_ERROR_EVENT = 'prepplogger:auth-error';

export function dispatchAuthError() {
  window.dispatchEvent(new CustomEvent(AUTH_ERROR_EVENT));
}

// ==================== App API ====================

async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    credentials: 'include',
  });

  // Handle auth errors - dispatch event to trigger login redirect
  if (response.status === 401 || response.status === 403) {
    console.log('Auth error detected, dispatching auth error event');
    dispatchAuthError();
    throw new Error('Authentication required');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response;
}

export async function getApps(): Promise<AppInfo[]> {
  const response = await fetchWithAuth('/apps');
  const data = await response.json();
  return data.apps;
}

export async function getConfiguredApps(): Promise<AppInfo[]> {
  const response = await fetchWithAuth('/apps/configured');
  const data = await response.json();
  return data.apps;
}

export async function addApp(app: { name: string }): Promise<void> {
  await fetchWithAuth('/apps', {
    method: 'POST',
    body: JSON.stringify(app),
  });
}

export async function removeApp(name: string): Promise<void> {
  await fetchWithAuth(`/apps/${encodeURIComponent(name)}`, {
    method: 'DELETE',
  });
}

export async function getAppStatus(name: string): Promise<{
  name: string;
  stats: {
    totalLogs: number;
    oldestLog?: string;
    newestLog?: string;
    databaseSize: number;
  } | null;
}> {
  const response = await fetchWithAuth(`/apps/${encodeURIComponent(name)}/status`);
  return response.json();
}

export async function getLogDetails(logId: string): Promise<LogEntryDetails> {
  const response = await fetchWithAuth(`/logs/details/${encodeURIComponent(logId)}`);
  return response.json();
}

export async function searchLogs(app: string, params: SearchParams): Promise<SearchResult> {
  const searchParams = new URLSearchParams();

  if (params.q) searchParams.set('q', params.q);
  if (params.from) searchParams.set('from', params.from);
  if (params.to) searchParams.set('to', params.to);
  if (params.level && params.level.length > 0) searchParams.set('level', params.level.join(','));
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.offset) searchParams.set('offset', String(params.offset));

  const response = await fetchWithAuth(`/logs/${encodeURIComponent(app)}?${searchParams}`);
  return response.json();
}
